import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState, StatusPill } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { PhoneCall, Plus, PlugsConnected, ListNumbers, Power, MagnifyingGlass, PencilSimple, Trash } from "@phosphor-icons/react";

const EMPTY = { name: "", host: "", sip_port: "5060", transport: "UDP", cred_username: "", cred_secret: "", region: "", notes: "" };

export default function VicidialServers() {
  const { user } = useAuth();
  const isCustomer = user?.role === "customer";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);
  const [campDlg, setCampDlg] = useState(null);
  const [campRows, setCampRows] = useState([]);
  const [campLoading, setCampLoading] = useState(false);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await api.get("/servers/vicidial")).data); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEdit(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => {
    setEdit(s);
    setForm({ name: s.name, host: s.host, sip_port: String(s.sip_port || 5060), transport: s.transport || "UDP",
      cred_username: s.cred_username || "", cred_secret: "", region: s.region || "", notes: s.notes || "" });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = { name: form.name, host: form.host, sip_port: parseInt(form.sip_port || "5060"),
        transport: form.transport, cred_username: form.cred_username || null,
        region: form.region || null, notes: form.notes || null };
      if (form.cred_secret) body.cred_secret = form.cred_secret;
      if (edit) await api.patch(`/servers/vicidial/${edit.id}`, body);
      else await api.post("/servers/vicidial", body);
      toast.success(edit ? "Server updated" : "Server added");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const act = async (fn, id) => { setBusy(id); try { await fn(); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(null); } };
  const del = (id) => act(async () => { await api.delete(`/servers/vicidial/${id}`); toast.success("Server removed"); }, id);
  const toggleStatus = (s) => act(async () => { await api.post(`/servers/vicidial/${s.id}/status`, { status: s.status === "active" ? "inactive" : "active" }); toast.success("Status updated"); }, s.id);
  const test = (s) => act(async () => { const { data } = await api.post(`/servers/vicidial/${s.id}/test`); toast.success(`Reachable · ${data.latency_ms} ms (simulated)`); }, s.id);
  const viewCampaigns = async (s) => {
    setCampDlg(s); setCampRows([]); setCampLoading(true);
    try { setCampRows((await api.get(`/servers/vicidial/${s.id}/campaigns`)).data); } catch { setCampRows([]); } finally { setCampLoading(false); }
  };

  const filtered = useMemo(() => {
    if (isCustomer || !query.trim()) return rows;
    const q = query.toLowerCase();
    return rows.filter((s) =>
      (s.customer_name || "").toLowerCase().includes(q) ||
      (s.name || "").toLowerCase().includes(q) ||
      (s.host || "").toLowerCase().includes(q) ||
      (s.status || "").toLowerCase().includes(q));
  }, [rows, query, isCustomer]);

  // ---------------- CUSTOMER: full CRUD ----------------
  if (isCustomer) {
    return (
      <div>
        <PageHeader title="VICIdial Servers" subtitle="Add and manage your own VICIdial dialer nodes">
          <Button onClick={openNew} data-testid="create-vicidial-btn" className="rounded-lg"><Plus className="w-4 h-4 mr-1.5" weight="bold" /> Add Server</Button>
        </PageHeader>

        {loading ? <div className="text-sm text-muted-foreground">Loading…</div>
          : rows.length === 0 ? (
            <EmptyState testid="vicidial-empty" icon={PhoneCall} title="No VICIdial servers" description="Add your VICIdial dialer nodes, then select one when creating a campaign." />
          ) : (
            <div className="rounded-xl border border-border bg-card card-shadow overflow-hidden" data-testid="vicidial-table">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Name</TableHead><TableHead>IP Address</TableHead><TableHead>SIP Port</TableHead>
                  <TableHead>Transport</TableHead><TableHead className="text-center">Campaigns</TableHead>
                  <TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {rows.map((s) => (
                    <TableRow key={s.id} className="hover:bg-muted/40" data-testid={`vicidial-row-${s.id}`}>
                      <TableCell className="font-medium">{s.name}</TableCell>
                      <TableCell className="font-mono text-sm">{s.host}</TableCell>
                      <TableCell className="tabular-nums">{s.sip_port || 5060}</TableCell>
                      <TableCell className="text-sm">{s.transport || "UDP"}</TableCell>
                      <TableCell className="text-center tabular-nums" data-testid={`vicidial-campcount-${s.id}`}>{s.campaign_count ?? 0}</TableCell>
                      <TableCell><StatusPill status={s.status} /></TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex items-center gap-1">
                          <Button variant="ghost" size="sm" className="rounded-lg px-2" onClick={() => test(s)} disabled={busy === s.id} data-testid={`vicidial-test-${s.id}`} aria-label="Test connectivity" title="Test connectivity"><PlugsConnected className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" className="rounded-lg px-2" onClick={() => viewCampaigns(s)} data-testid={`vicidial-campaigns-${s.id}`} aria-label="Campaigns and prefixes" title="Campaigns & prefixes"><ListNumbers className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" className="rounded-lg px-2" onClick={() => toggleStatus(s)} disabled={busy === s.id} data-testid={`vicidial-toggle-${s.id}`} aria-label={s.status === "active" ? "Deactivate server" : "Activate server"} title={s.status === "active" ? "Deactivate" : "Activate"}><Power className="w-4 h-4" /></Button>
                          <Button variant="outline" size="sm" className="rounded-lg h-8 w-8 p-0" onClick={() => openEdit(s)} data-testid={`edit-vicidial-${s.id}`} title="Edit" aria-label="Edit"><PencilSimple className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive hover:text-destructive" onClick={() => del(s.id)} data-testid={`delete-vicidial-${s.id}`} title="Delete" aria-label="Delete"><Trash className="w-4 h-4" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent data-testid="vicidial-dialog">
            <DialogHeader><DialogTitle>{edit ? "Edit" : "Add"} VICIdial server</DialogTitle>
              <DialogDescription>Your own VICIdial dialer node. Connectivity test is simulated.</DialogDescription></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2"><Label>Server Name</Label><Input data-testid="vicidial-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2 col-span-2"><Label>IP Address</Label><Input data-testid="vicidial-host" value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} placeholder="157.90.100.20" /></div>
                <div className="space-y-2"><Label>SIP Port</Label><Input data-testid="vicidial-sip-port" type="number" value={form.sip_port} onChange={(e) => setForm({ ...form, sip_port: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Transport</Label>
                  <Select value={form.transport} onValueChange={(v) => setForm({ ...form, transport: v })}>
                    <SelectTrigger data-testid="vicidial-transport"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="UDP">UDP</SelectItem><SelectItem value="TCP">TCP</SelectItem><SelectItem value="TLS">TLS</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Region <span className="text-muted-foreground">(optional)</span></Label><Input data-testid="vicidial-region" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Credential username <span className="text-muted-foreground">(optional)</span></Label><Input data-testid="vicidial-cred-user" value={form.cred_username} onChange={(e) => setForm({ ...form, cred_username: e.target.value })} /></div>
                <div className="space-y-2"><Label>Credential secret <span className="text-muted-foreground">(optional)</span></Label><Input data-testid="vicidial-cred-secret" type="password" value={form.cred_secret} onChange={(e) => setForm({ ...form, cred_secret: e.target.value })} placeholder={edit?.has_cred_secret ? "•••••• (leave blank to keep)" : ""} /></div>
              </div>
            </div>
            <DialogFooter><Button onClick={save} disabled={saving} data-testid="submit-vicidial-btn" className="rounded-lg">{saving ? "Saving…" : "Save"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        <CampaignsDialog campDlg={campDlg} setCampDlg={setCampDlg} campRows={campRows} campLoading={campLoading} showCustomer={false} />
      </div>
    );
  }

  // ---------------- ADMIN / RESELLER: read-only ----------------
  return (
    <div>
      <PageHeader title="VICIdial Servers" subtitle="Read-only view of all customer-owned VICIdial servers">
        <div className="relative">
          <MagnifyingGlass className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="vicidial-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Filter by customer, name, IP, status" className="pl-9 w-72" />
        </div>
      </PageHeader>

      {loading ? <div className="text-sm text-muted-foreground">Loading…</div>
        : rows.length === 0 ? (
          <EmptyState testid="vicidial-empty" icon={PhoneCall} title="No VICIdial servers" description="Customers have not added any VICIdial servers yet." />
        ) : (
          <div className="rounded-xl border border-border bg-card card-shadow overflow-hidden" data-testid="vicidial-admin-table">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Customer</TableHead><TableHead>Server</TableHead><TableHead>IP Address</TableHead>
                <TableHead>Port</TableHead><TableHead>Transport</TableHead><TableHead>Status</TableHead>
                <TableHead className="text-center">Campaigns</TableHead><TableHead>Prefixes</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {filtered.map((s) => (
                  <TableRow key={s.id} className="hover:bg-muted/40" data-testid={`vicidial-admin-row-${s.id}`}>
                    <TableCell className="font-medium" data-testid={`vicidial-owner-${s.id}`}>{s.customer_name || "—"}</TableCell>
                    <TableCell>{s.name}</TableCell>
                    <TableCell className="font-mono text-sm">{s.host}</TableCell>
                    <TableCell className="tabular-nums">{s.sip_port || 5060}</TableCell>
                    <TableCell className="text-sm">{s.transport || "UDP"}</TableCell>
                    <TableCell><StatusPill status={s.status} /></TableCell>
                    <TableCell className="text-center">
                      <button className="text-sm tabular-nums underline-offset-2 hover:underline" onClick={() => viewCampaigns(s)} data-testid={`vicidial-campaigns-${s.id}`}>{s.campaign_count ?? 0}</button>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground max-w-[220px] truncate" title={(s.campaign_prefixes || []).join(", ")}>{(s.campaign_prefixes || []).join(", ") || "—"}</TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-6">No servers match your filter.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}

      <CampaignsDialog campDlg={campDlg} setCampDlg={setCampDlg} campRows={campRows} campLoading={campLoading} showCustomer={true} />
    </div>
  );
}

function CampaignsDialog({ campDlg, setCampDlg, campRows, campLoading, showCustomer }) {
  return (
    <Dialog open={!!campDlg} onOpenChange={(v) => !v && setCampDlg(null)}>
      <DialogContent data-testid="vicidial-campaigns-dialog">
        <DialogHeader><DialogTitle>Campaigns on {campDlg?.name}</DialogTitle>
          <DialogDescription>Campaigns routed through this server and their routing prefixes.</DialogDescription></DialogHeader>
        <div className="py-2 space-y-2 max-h-[50vh] overflow-y-auto">
          {campLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {!campLoading && campRows.length === 0 && <p className="text-sm text-muted-foreground">No campaigns use this server.</p>}
          {!campLoading && campRows.map((c) => (
            <div key={c.id} className="flex items-center justify-between rounded-lg border border-border p-3" data-testid={`vicidial-camp-${c.id}`}>
              <div>
                <p className="font-medium text-sm">{c.name}</p>
                <p className="text-xs text-muted-foreground">{showCustomer ? `${c.customer_name || "—"} · ` : ""}prefix <span className="font-mono text-foreground">{c.campaign_prefix || "—"}</span></p>
              </div>
              <StatusPill status={c.status} />
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
